import {
  AbstractMesh,
  Axis,
  BoundingBox,
  Matrix,
  Mesh,
  PBRMaterial,
  PhysicsShapeType,
  Quaternion,
  Ray,
  StandardMaterial,
  Vector2,
  Vector3,
} from '@babylonjs/core';
import { AssetBasedConfig, AssetObject, DistributionConfig } from './AssetObject';
import { DungeonRoom } from './DungeonGenerator';
import { EntityGenerator, Level, PathInfo, WayMesh } from './Level';
import { NumberRange } from './LevelConfig';
import { AssetObjectConfig, ExistingMeshConfig, MeshFactory } from './MeshFactory';
import { Platform } from './PlatformFactory';

/**
 * Distribution mode for decorations
 * - 'zone': Random placement within zone (e.g., trees on both sides of path)
 * - 'fixed': Fixed interval placement (e.g., guardrails)
 * - 'path-aligned': Aligned to path direction (e.g., road markings)
 */
export type DistributionMode = 'zone' | 'fixed' | 'absolute' | 'mesh';

export type MeshSurfaceDistribution = 'random' | 'center' | 'corners' | 'edges' | 'sides';

export interface AbsolutePlacement {
  /**
   * World position for 'absolute' mode
   */
  position?: Vector3;

  /**
   * World rotation for 'absolute' mode
   */
  rotation?: Vector3;

  /**
   * World scale for 'absolute' mode
   */
  scale?: Vector3;
}

export interface DecorationDistributionConfig extends DistributionConfig {
  /**
   * Distribution mode
   */
  mode?: DistributionMode;

  /**
   * Zone for decoration (only for 'zone' mode)
   * min = left of the path (negative = left)
   * max = right of the path (positive = right)
   */
  zone?: NumberRange;

  /**
   * Dead zone for decoration - no decoration in this zone
   * Useful to keep path clear while placing on sides
   */
  deadZone?: NumberRange;

  /**
   * Offset from path center (for 'fixed' and 'path-aligned' modes)
   */
  offset?: Vector3;

  /**
   * Placement radius around a path point (for 'zone' mode)
   */
  radius?: number;

  /**
   * Number of decoration instances per path segment (for 'zone' and 'mesh' mode)
   */
  density?: number;

  /**
   * Y Rotation range in radians (0 to 2*PI)
   */
  rotationY?: NumberRange;

  /**
   * Random rotation on all axes (overrides rotationY)
   */
  randomRotation?: boolean;

  /**
   * Align rotation to path direction (useful for guardrails)
   */
  alignToPath?: boolean;

  /**
   * Rotation offset in radians to add when alignToPath is true
   * Use this to adjust for mesh orientation (e.g., Math.PI/2 for 90° offset)
   */
  rotationOffset?: number;

  /**
   * Uniform scaling range
   */
  scale?: NumberRange;

  /**
   * Use level Y position, otherwise use offset.y + path.y
   */
  useLevelYPosition?: boolean;

  /**
   * Enable physics for decoration
   */
  physics?: boolean;

  /**
   * Alpha for decoration
   */
  alpha?: number;

  /**
   * Skip decoration if path point is occupied
   */
  skipOccupied?: boolean;

  /**
   * Use rolling distribution for decoration, if we have multiple assets, distribute them in a rolling manner
   */
  rolling?: boolean;

  /**
   * Use random distribution for decoration, if we have multiple assets, distribute them in a random manner
   */
  random?: boolean;

  /**
   * Absolute placements for decoration
   */
  absolutePlacements?: AbsolutePlacement[];

  /**
   * Where to place decoration on the mesh (for 'mesh' mode)
   */
  meshSurface?: MeshSurfaceDistribution;

  /**
   * Align decoration up-vector with surface normal (for 'mesh' mode)
   */
  alignToNormal?: boolean;

  /**
   * Pick from path for mesh surface decoration
   */
  meshSurfacePickFromPath?: boolean;

  /**
   * Spread of the decoration on the mesh (for 'mesh' mode)
   */
  spread?: number;

  /**
   * Align rotation to face direction (for 'zone' mode)
   */
  alignToFace?: boolean;
}

export interface DecorationConfig extends AssetBasedConfig<DecorationDistributionConfig> {}

/**
 * Generator for generating decorations inside of a level
 */
export class DecorationGenerator implements EntityGenerator<DecorationConfig> {
  /**
   * Cache for decoration master meshes - stores the first clone per asset
   * to avoid issues with getMeshes() modifying source meshes
   */
  decorationMasterMeshes: Map<string, Mesh> = new Map();

  /**
   * Cache for valid sides of a mesh to avoid re-calculating for every decoration
   */
  private meshValidSidesCache: Map<string, string[]> = new Map();

  constructor(public level: Level) {}

  update(deltaTime: number): void {}

  dispose(): void {
    for (const mesh of this.decorationMasterMeshes.values()) {
      mesh.dispose();
    }
    this.decorationMasterMeshes.clear();
  }

  async generate(config: DecorationConfig): Promise<void> {
    // Process each distribution config with unique index
    for (let i = 0; i < config.distributions.length; i++) {
      await this.generateDistribution(config, config.distributions[i], i);
    }
  }

  /**
   * Create decoration instances for a single distribution configuration
   */
  async generateDistribution(
    config: DecorationConfig,
    distribution: DecorationDistributionConfig,
    distributionIndex: number
  ): Promise<void> {
    // Find asset object for this type
    const assetObjects = config.assetObjects.filter((ao) => distribution.types.includes(ao.type));
    if (assetObjects.length < 1) {
      throw new Error(`[Level] No asset object found for decoration types: ${distribution.types}`);
    }
    // if (assetObjects.length > 1 && distribution.mode === 'fixed') {
    //   throw new Error(`[Level] Multiple asset objects found for fixed decoration types: ${distribution.types}`);
    // }

    for (let j = 0; j < assetObjects.length; j++) {
      const assetObject = assetObjects[j];
      // Get base mesh for thin instances - use unique index to avoid conflicts
      const baseMesh = this.getDecorationBaseMesh(assetObject, distributionIndex);
      if (!baseMesh) {
        throw new Error(`[Level] Could not get base mesh for decoration: ${assetObject.asset}`);
      }

      if (distribution.alpha) {
        const mat = baseMesh.material as PBRMaterial;
        if (mat) {
          mat.transparencyMode = StandardMaterial.MATERIAL_ALPHABLEND;
          mat.alpha = distribution.alpha;
          mat.emissiveColor = mat.emissiveColor.scale(distribution.alpha);
        }
      }

      // Collect transforms for all instances
      const transforms: Matrix[] = [];
      const mode = distribution.mode || 'zone';
      const interval = distribution.interval || 1;
      const minPoint = distribution.minPoint || 0;
      const maxPoint = distribution.maxPoint || this.level.pathPoints.length - 1;

      if (distribution.mode === 'absolute') {
        // absolute mode
        this.createAbsoluteDecoration(distribution, transforms);
      } else {
        // non-absolute modes
        for (let i = 0; i < this.level.pathPoints.length; i += interval) {
          if (i < minPoint || i > maxPoint) {
            continue;
          }

          if (distribution.skipOccupied && this.level.occupiedPathIndexes.includes(i)) {
            continue;
          }

          // If we have multiple assets, distribute them in a rolling manner
          if (distribution.rolling && assetObjects.length > 1) {
            const stepIndex = Math.round((i - minPoint) / interval);
            if (stepIndex % assetObjects.length !== j) {
              continue;
            }
          }

          // If we have multiple assets, distribute them in a random manner
          if (distribution.random && assetObjects.length > 1) {
            const randomIndex = this.level.integerInRange(0, assetObjects.length - 1);
            if (randomIndex !== j) {
              continue;
            }
          }

          const probability = this.level.rng.uFloat32();
          if (distribution.probability && probability > distribution.probability) {
            continue;
          }
          if (distribution.skipProbability && probability < distribution.skipProbability) {
            continue;
          }

          const pathPoint = this.level.pathPoints[i];
          const pathInfo = this.level.getPathInfo(i);

          const platform: Platform = this.level.getPlatformByPathIndex(i);
          const wayMesh: WayMesh = this.level.getWayMeshByPathIndex(i);
          const room: DungeonRoom = this.level.getRoomByPathIndex(i);
          const mesh = room ? room.walls.floor : platform ? platform.mesh : wayMesh?.mesh;

          if (this.level.config.platform && !this.level.config.way) {
            if (!platform) {
              // no instance at this index because it would be placed in the air
              continue;
            }
            if (platform.config.moving || platform.config.crumbling) {
              // no instance on moving platforms, would require parenting, but only thin instances are created
              continue;
            }
          }

          switch (mode) {
            case 'zone':
              this.createZoneDecorations(pathPoint, pathInfo, distribution, transforms, mesh);
              break;
            case 'fixed':
              this.createFixedDecoration(pathPoint, pathInfo, distribution, transforms, mesh);
              break;
            case 'mesh':
              this.createMeshDecorations(pathPoint, pathInfo, distribution, transforms, mesh);
              break;
          }
        }
      }

      // Create thin instances from transforms
      if (transforms.length > 0) {
        // Decoration meshes are visual only
        baseMesh.isPickable = false;
        baseMesh.receiveShadows = true;
        // Thin instances can't be individually filtered, so exclude from distance filter
        this.level.addShadowCaster(baseMesh, true);

        MeshFactory.createThinInstances(baseMesh, transforms);

        // Create physics bodies if requested
        if (distribution.physics) {
          console.log(`[Level] Creating physics bodies for ${assetObject.type}`);
          for (let i = 0; i < transforms.length; i++) {
            const matrix = transforms[i];
            const clone = baseMesh.clone(`${baseMesh.name}_physics_${i}`, null);

            // Set transform
            if (!clone.rotationQuaternion) {
              clone.rotationQuaternion = Quaternion.Identity();
            }
            matrix.decompose(clone.scaling, clone.rotationQuaternion, clone.position);

            // Configure physics
            clone.isVisible = false;
            clone.checkCollisions = true;

            const physicsConfig = ExistingMeshConfig.builder().mesh(clone).physicsShapeType(PhysicsShapeType.MESH).build();

            MeshFactory.configureMesh(clone, physicsConfig, this.level);

            this.level.collisionMeshes.push(clone);
          }
        }
      }
    }
  }

  /**
   * Get or create the base mesh for decoration thin instances
   * Uses AssetObjectConfig.buildMesh() for consistent mesh creation
   * Uses a cache to avoid issues with getMeshes() modifying source meshes
   */
  private getDecorationBaseMesh(assetObject: AssetObject, distributionIndex: number = 0): Mesh | null {
    const cacheKey = `${assetObject.asset}_${assetObject.name || 'all'}`;

    // Check if we already have a master mesh for this asset
    let masterMesh = this.decorationMasterMeshes.get(cacheKey);

    if (!masterMesh) {
      // Ensure clone is enabled so we don't modify the original
      assetObject.clone = true;

      // Create master mesh using AssetObjectConfig.buildMesh()
      const assetObjectConfig = AssetObjectConfig.builder().object(assetObject).name(`decoration_master_${cacheKey}`).build();

      masterMesh = assetObjectConfig.buildMesh(this.level);

      if (!masterMesh) {
        console.warn(`[Level] Failed to build mesh for decoration: ${assetObject.asset}`);
        return null;
      }

      // Hide master mesh - only thin instances will be visible
      masterMesh.isVisible = false;
      masterMesh.setEnabled(false);

      // Cache the master mesh
      this.decorationMasterMeshes.set(cacheKey, masterMesh);
    }

    // Clone from master for this distribution
    const baseMesh = masterMesh.clone(`decoration_base_${assetObject.asset}_${distributionIndex}`, null);

    // Make geometry unique so thin instances don't share the same buffer
    baseMesh.makeGeometryUnique();

    // Ensure mesh is enabled and visible
    baseMesh.isVisible = true;
    baseMesh.setEnabled(true);

    return baseMesh;
  }

  /**
   * Create decorations in zone mode - random placement within zone
   */
  private createZoneDecorations(
    pathPoint: Vector3,
    pathInfo: PathInfo,
    config: DecorationDistributionConfig,
    transforms: Matrix[],
    mesh: AbstractMesh
  ): void {
    const tangent: Vector3 = pathInfo.tangent;
    const lateral: Vector3 = pathInfo.lateral;
    const density = config.density || 1;
    const zone = config.zone || { min: -10, max: 10 };
    const deadZone = config.deadZone;

    for (let d = 0; d < density; d++) {
      // Random offset within zone, avoiding dead zone
      let lateralOffset: number;
      let attempts = 0;

      do {
        lateralOffset = this.level.floatInRange(zone.min, zone.max);
        attempts++;
      } while (deadZone && lateralOffset >= deadZone.min && lateralOffset <= deadZone.max && attempts < 10);

      // Skip if we couldn't find a valid position
      if (deadZone && lateralOffset >= deadZone.min && lateralOffset <= deadZone.max) {
        continue;
      }

      let position = pathPoint.clone();
      if (this.level.config.platform) {
        position = this.level.getMaxPathPosition(pathInfo.pathIndex);
      }

      if (config.radius) {
        const xAreaOffset = this.level.floatInRange(-config.radius, config.radius);
        const zAreaOffset = this.level.floatInRange(-config.radius, config.radius);
        position = position.add(new Vector3(xAreaOffset, 0, zAreaOffset));
      }

      position = position.add(lateral.scale(lateralOffset));

      // Get height at position
      const groundY = this.level.getHeightAtCoordinates(position.x, position.z);
      position.y = config.useLevelYPosition ? groundY : position.y;

      if (config.offset?.y) {
        position.y += config.offset.y;
      }

      const scale = this.getDecorationScale(config);
      let rotationQuaternion: Quaternion;

      // Align to face normal if configured
      if (config.alignToFace) {
        const faceInfo = this.level.getGroundFaceAtCoordinates(position.x, position.z);

        if (faceInfo && faceInfo.normal) {
          const normal = faceInfo.normal;

          const dot = Vector3.Dot(normal, Vector3.Up());

          // Calculate tangent and bitangent to create rotation matrix from normal
          let tangentVec = dot < 0.99 && dot > -0.99 ? Vector3.Cross(normal, Vector3.Up()) : Vector3.Cross(normal, Vector3.Right());
          tangentVec.normalize();

          let bitangent = Vector3.Cross(normal, tangentVec);

          // To make objects sit on their BOTTOM (-Y) instead of standing on TOP (+Y),
          // we negate the bitangent to flip the coordinate system
          let alignRotation = Quaternion.RotationQuaternionFromAxis(tangentVec, normal, bitangent.negate());
          rotationQuaternion = alignRotation;
        } else {
          // Fallback to default rotation if face info not available
          const rotation = this.getDecorationRotation(config, tangent);
          rotationQuaternion = Quaternion.FromEulerAngles(rotation.x, rotation.y, rotation.z);
        }
      } else {
        // Use standard rotation calculation
        const rotation = this.getDecorationRotation(config, tangent);
        rotationQuaternion = Quaternion.FromEulerAngles(rotation.x, rotation.y, rotation.z);
      }

      const matrix = Matrix.Compose(new Vector3(scale, scale, scale), rotationQuaternion, position);
      transforms.push(matrix);
    }
  }

  /**
   * Create decorations in mesh mode - placement on mesh surface using top-down projection
   */
  private createMeshDecorations(
    pathPoint: Vector3,
    pathInfo: PathInfo,
    config: DecorationDistributionConfig,
    transforms: Matrix[],
    mesh: AbstractMesh
  ): void {
    if (!mesh) {
      return;
    }

    const targetMeshes = this.getTargetMeshes(mesh);
    if (targetMeshes.length === 0) {
      return;
    }

    const surfaceDistribution = config.meshSurface || 'random';
    const density = config.density || 1;

    // Calculate combined bounding box
    let min = new Vector3(Number.MAX_VALUE, Number.MAX_VALUE, Number.MAX_VALUE);
    let max = new Vector3(-Number.MAX_VALUE, -Number.MAX_VALUE, -Number.MAX_VALUE);

    for (const targetMesh of targetMeshes) {
      targetMesh.computeWorldMatrix(true);
      const boundingInfo = targetMesh.getBoundingInfo();
      const boundingBox = boundingInfo.boundingBox;
      min = Vector3.Minimize(min, boundingBox.minimumWorld);
      max = Vector3.Maximize(max, boundingBox.maximumWorld);
    }

    // Create a bounding box representing the union
    const boundingBox = new BoundingBox(min, max);

    // Ray direction (down)
    const rayDirection = new Vector3(0, -1, 0);

    // Calculate total attempts to find valid positions
    const maxAttempts = density * 10;

    // Get valid sides for 'sides' mode
    const validSides = this.getValidSides(mesh);

    let createdCount = 0;

    for (let i = 0; i < maxAttempts && createdCount < density; i++) {
      const xz = this.getRandomPositionInBoundingBox(boundingBox, surfaceDistribution, pathInfo.tangent, validSides, config.spread);

      const rayOrigin = new Vector3(xz.x, config.meshSurfacePickFromPath ? pathPoint.y : max.y + 1, xz.y);
      const rayLength = config.meshSurfacePickFromPath ? pathPoint.y - min.y + 2 : max.y - min.y + 2;
      const ray = new Ray(rayOrigin, rayDirection, rayLength);

      // Check against all target meshes
      const pickingInfos = ray.intersectsMeshes(targetMeshes as any); // Type cast if needed, though intersectsMeshes takes DeepImmutable<AbstractMesh>[]

      if (pickingInfos.length > 0 && pickingInfos[0].hit && pickingInfos[0].pickedPoint) {
        const pickingInfo = pickingInfos[0];
        const normal = pickingInfo.getNormal(true, true);
        const dot = normal ? Vector3.Dot(normal, Vector3.Up()) : 0;

        // Accept both normal orientations (abs > 0.5) since meshes may have inverted normals
        if (normal && Math.abs(dot) > 0.5) {
          // Flip normal if it points downward (inverted mesh)
          if (dot < 0) {
            normal.negateInPlace();
          }
          let position = pickingInfo.pickedPoint.clone();

          if (config.offset) {
            if (config.alignToNormal) {
              position.addInPlace(normal.scale(config.offset.y));
              position.addInPlace(new Vector3(config.offset.x, 0, config.offset.z));
            } else {
              position.addInPlace(config.offset);
            }
          }

          const scale = this.getDecorationScale(config);
          let rotationQuaternion: Quaternion;

          if (config.alignToNormal) {
            let tangent =
              Math.abs(Vector3.Dot(normal, Vector3.Up())) < 0.99
                ? Vector3.Cross(normal, Vector3.Up())
                : Vector3.Cross(normal, Vector3.Right());
            tangent.normalize();
            let bitangent = Vector3.Cross(normal, tangent);

            let alignRotation = Quaternion.RotationQuaternionFromAxis(tangent, normal, bitangent);

            if (config.randomRotation) {
              const roll = this.level.floatInRange(0, Math.PI * 2);
              alignRotation = alignRotation.multiply(Quaternion.RotationAxis(Axis.Y, roll));
            } else if (config.rotationY) {
              const yRot = this.level.floatInRange(config.rotationY.min, config.rotationY.max);
              alignRotation = alignRotation.multiply(Quaternion.RotationAxis(Axis.Y, yRot));
            }
            rotationQuaternion = alignRotation;
          } else {
            const rotation = this.getDecorationRotation(config, pathInfo.tangent);
            rotationQuaternion = Quaternion.FromEulerAngles(rotation.x, rotation.y, rotation.z);
          }

          const matrix = Matrix.Compose(new Vector3(scale, scale, scale), rotationQuaternion, position);
          transforms.push(matrix);
          createdCount++;
        }
      }
    }
  }

  /**
   * Get all target meshes for decoration, including children if it's a container
   */
  private getTargetMeshes(mesh: AbstractMesh): AbstractMesh[] {
    const meshes: AbstractMesh[] = [];

    // If the mesh itself has geometry, add it
    if (mesh instanceof Mesh && mesh.isEnabled() && mesh.getTotalVertices() > 0) {
      meshes.push(mesh);
    } else if (mesh.constructor.name === 'GroundMesh' && mesh.isEnabled()) {
      // Special case for GroundMesh which might verify geometry differently or be just valid
      meshes.push(mesh);
    }

    // Check children
    const children = mesh.getChildMeshes(false);
    for (const child of children) {
      if (child.isEnabled() && child instanceof Mesh && child.getTotalVertices() > 0) {
        meshes.push(child);
      }
    }

    return meshes;
  }

  /**
   * Determine which sides of the mesh intersection are "valid" (not cut by the path)
   * Returns ['minX', 'maxX', 'minZ', 'maxZ'] excluding any that are intersected by path entry/exit
   */
  private getValidSides(mesh: AbstractMesh): string[] {
    const meshId = mesh.uniqueId.toString();
    if (this.meshValidSidesCache.has(meshId)) {
      return this.meshValidSidesCache.get(meshId)!;
    }

    const boundingInfo = mesh.getBoundingInfo();
    const min = boundingInfo.boundingBox.minimumWorld;
    const max = boundingInfo.boundingBox.maximumWorld;

    // We only care about X and Z intersections for walls
    // We will check all segments of the path to see if they intersect the 4 restricted planes
    // Plane minX: x = min.x, min.z <= z <= max.z
    // Plane maxX: x = max.x, min.z <= z <= max.z
    // Plane minZ: z = min.z, min.x <= x <= max.x
    // Plane maxZ: z = max.z, min.x <= x <= max.x

    const excludedSides: Set<string> = new Set();
    const padding = 0.5; // Small tolerance for the "within limits" check

    // Optimization: find start/end indices that are possibly near the mesh
    // Accessing level.pathPoints directly
    const points = this.level.pathPoints;

    // We can't easily optimize the range without spatial index, but linear scan is acceptable for now
    // or we can just scan segments.
    // To be safer and efficient, we only check segments that have at least one point "near" the mesh bounds.
    // Or simpler: just check all segments relative to the specific planes.

    // Check segments
    for (let i = 0; i < points.length - 1; i++) {
      const p1 = points[i];
      const p2 = points[i + 1];

      // Quick AABB check of the segment against the mesh AABB (extended slightly)
      const segMinX = Math.min(p1.x, p2.x);
      const segMaxX = Math.max(p1.x, p2.x);
      const segMinZ = Math.min(p1.z, p2.z);
      const segMaxZ = Math.max(p1.z, p2.z);

      if (segMaxX < min.x - padding || segMinX > max.x + padding || segMaxZ < min.z - padding || segMinZ > max.z + padding) {
        continue;
      }

      // Check MinX Plane intersection
      if ((p1.x < min.x && p2.x >= min.x) || (p1.x >= min.x && p2.x < min.x)) {
        // Calculate Z intersection
        const t = (min.x - p1.x) / (p2.x - p1.x);
        const z = p1.z + t * (p2.z - p1.z);
        if (z >= min.z - padding && z <= max.z + padding) {
          excludedSides.add('minX');
        }
      }

      // Check MaxX Plane intersection
      if ((p1.x < max.x && p2.x >= max.x) || (p1.x >= max.x && p2.x < max.x)) {
        const t = (max.x - p1.x) / (p2.x - p1.x);
        const z = p1.z + t * (p2.z - p1.z);
        if (z >= min.z - padding && z <= max.z + padding) {
          excludedSides.add('maxX');
        }
      }

      // Check MinZ Plane intersection
      if ((p1.z < min.z && p2.z >= min.z) || (p1.z >= min.z && p2.z < min.z)) {
        const t = (min.z - p1.z) / (p2.z - p1.z);
        const x = p1.x + t * (p2.x - p1.x);
        if (x >= min.x - padding && x <= max.x + padding) {
          excludedSides.add('minZ');
        }
      }

      // Check MaxZ Plane intersection
      if ((p1.z < max.z && p2.z >= max.z) || (p1.z >= max.z && p2.z < max.z)) {
        const t = (max.z - p1.z) / (p2.z - p1.z);
        const x = p1.x + t * (p2.x - p1.x);
        if (x >= min.x - padding && x <= max.x + padding) {
          excludedSides.add('maxZ');
        }
      }
    }

    const allSides = ['minX', 'maxX', 'minZ', 'maxZ'];
    const validSides = allSides.filter((s) => !excludedSides.has(s));

    this.meshValidSidesCache.set(meshId, validSides);
    return validSides;
  }

  private getRandomPositionInBoundingBox(
    boundingBox: BoundingBox,
    mode: MeshSurfaceDistribution,
    tangent: Vector3,
    validSides: string[],
    spread: number = 0.2
  ): Vector2 {
    const min = boundingBox.minimumWorld;
    const max = boundingBox.maximumWorld;
    const width = max.x - min.x;
    const depth = max.z - min.z;

    let x = 0;
    let z = 0;

    switch (mode) {
      case 'center': {
        // Center with some spread (e.g. 20% of size)
        const spreadX = width * spread;
        const spreadZ = depth * spread;
        x = (min.x + max.x) / 2 + this.level.floatInRange(-spreadX / 2, spreadX / 2);
        z = (min.z + max.z) / 2 + this.level.floatInRange(-spreadZ / 2, spreadZ / 2);
        break;
      }
      case 'edges': {
        // Edge means growing inwards from the border by spread amount
        // We pick an axis (X or Z) to be at the edge, the other is random
        const spreadX = width * spread;
        const spreadZ = depth * spread;

        if (this.level.rng.uFloat32() < 0.5) {
          // X edge
          x = this.level.rng.uFloat32() < 0.5 ? min.x + this.level.floatInRange(0, spreadX) : max.x - this.level.floatInRange(0, spreadX);
          z = min.z + this.level.floatInRange(0, depth);
        } else {
          // Z edge
          x = min.x + this.level.floatInRange(0, width);
          z = this.level.rng.uFloat32() < 0.5 ? min.z + this.level.floatInRange(0, spreadZ) : max.z - this.level.floatInRange(0, spreadZ);
        }
        break;
      }
      case 'sides': {
        // Sides means the edges parallel to the path, EXCLUDING those cut by the path

        // calculate the mathematical intersection of every path segment against the 4 vertical planes of the mesh.
        // If a path segment passes through x = min.x, then the minX side is strictly marked as "cut" and excluded from decoration.
        // This should correctly handle all cases, including straight lines and curves, regardless of path point density.

        const spreadX = width * spread;
        const spreadZ = depth * spread;

        let side = 'none';

        if (validSides.length > 0) {
          side = validSides[this.level.integerInRange(0, validSides.length - 1)];
        }

        // Fallback or specific side handling
        if (side === 'minX') {
          x = min.x + this.level.floatInRange(0, spreadX);
          z = min.z + this.level.floatInRange(0, depth);
        } else if (side === 'maxX') {
          x = max.x - this.level.floatInRange(0, spreadX);
          z = min.z + this.level.floatInRange(0, depth);
        } else if (side === 'minZ') {
          x = min.x + this.level.floatInRange(0, width);
          z = min.z + this.level.floatInRange(0, spreadZ);
        } else if (side === 'maxZ') {
          x = min.x + this.level.floatInRange(0, width);
          z = max.z - this.level.floatInRange(0, spreadZ);
        } else {
          // Fallback if no valid sides (shouldn't happen usually unless fully contained path?)
          // Just default to random
          x = min.x + this.level.floatInRange(0, width);
          z = min.z + this.level.floatInRange(0, depth);
        }
        break;
      }
      case 'corners': {
        // Corners: circle around corner, clipped to box
        // We pick a corner first
        const cornerX = this.level.rng.uFloat32() < 0.5 ? min.x : max.x;
        const cornerZ = this.level.rng.uFloat32() < 0.5 ? min.z : max.z;

        const maxRadius = Math.min(width, depth) * spread; // Use spread as percentage of smallest dimension

        // Generate point in circle using polar coordinates
        const angle = this.level.floatInRange(0, Math.PI * 2);
        const radius = Math.sqrt(this.level.rng.uFloat32()) * maxRadius; // Square root for uniform distribution

        let candidateX = cornerX + Math.cos(angle) * radius;
        let candidateZ = cornerZ + Math.sin(angle) * radius;

        // Clamp to bounding box to simulate "clipped to surface"
        x = Math.max(min.x, Math.min(max.x, candidateX));
        z = Math.max(min.z, Math.min(max.z, candidateZ));
        break;
      }
      case 'random':
      default:
        x = min.x + this.level.floatInRange(0, width);
        z = min.z + this.level.floatInRange(0, depth);
        break;
    }

    return new Vector2(x, z);
  }

  private createAbsoluteDecoration(config: DecorationDistributionConfig, transforms: Matrix[]): void {
    for (const placement of config.absolutePlacements) {
      const rotation = placement.rotation.clone();
      const scale = placement.scale.clone();
      const position = placement.position.clone();
      const matrix = Matrix.Compose(scale, Quaternion.FromEulerAngles(rotation.x, rotation.y, rotation.z), position);
      transforms.push(matrix);
    }
  }

  /**
   * Create decoration in fixed mode - single placement at fixed offset
   */
  private createFixedDecoration(
    pathPoint: Vector3,
    pathInfo: PathInfo,
    config: DecorationDistributionConfig,
    transforms: Matrix[],
    mesh: AbstractMesh
  ): void {
    const offset = config.offset || new Vector3(5, 0, 0);
    const tangent: Vector3 = pathInfo.tangent;
    const lateral: Vector3 = pathInfo.lateral;

    let position = pathPoint.clone();
    if (this.level.config.platform) {
      position = this.level.getMaxPathPosition(pathInfo.pathIndex);
    }
    position = position.add(lateral.scale(offset.x)).add(tangent.scale(offset.z));

    // Get height at position and add Y offset
    const groundY = this.level.getHeightAtCoordinates(position.x, position.z);
    position.y = config.useLevelYPosition ? groundY : position.y;

    if (offset.y) {
      position.y += offset.y;
    }

    const rotation = this.getDecorationRotation(config, tangent);
    const scale = this.getDecorationScale(config);

    const matrix = Matrix.Compose(
      new Vector3(scale, scale, scale),
      Quaternion.FromEulerAngles(rotation.x, rotation.y, rotation.z),
      position
    );
    transforms.push(matrix);
  }

  /**
   * Get rotation for decoration based on config
   */
  private getDecorationRotation(config: DecorationDistributionConfig, tangent: Vector3): Vector3 {
    if (config.alignToPath) {
      return this.level.getPathRotation(tangent, config.rotationOffset);
    }

    if (config.randomRotation) {
      return new Vector3(
        this.level.floatInRange(0, Math.PI * 2),
        this.level.floatInRange(0, Math.PI * 2),
        this.level.floatInRange(0, Math.PI * 2)
      );
    }

    if (config.rotationY) {
      const yRotation = this.level.floatInRange(config.rotationY.min, config.rotationY.max);
      return new Vector3(0, yRotation, 0);
    }

    return Vector3.Zero();
  }

  /**
   * Get scale for decoration based on config
   */
  private getDecorationScale(config: DecorationDistributionConfig): number {
    if (config.scale) {
      return this.level.floatInRange(config.scale.min, config.scale.max);
    }
    return 1;
  }
}
